/**
 * Workspace API Client
 *
 * Handles all workspace-related API calls
 */

import { AppConfig } from '../auth/config';

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  role: 'owner' | 'admin' | 'member';
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkspaceMember {
  userId: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  email?: string;
  name?: string;
}

export interface CreateWorkspaceRequest {
  name: string;
  description?: string;
}

export interface UpdateWorkspaceRequest {
  name?: string;
  description?: string;
}

export interface AddMemberRequest {
  email: string;
  role?: 'admin' | 'member';
}

/**
 * Create the workspace API client
 */
export function createWorkspaceApi(config: AppConfig | null, getAccessToken: () => Promise<string | null>) {
  const baseUrl = config?.api?.endpoint || '';

  async function fetchWithAuth(path: string, options: RequestInit = {}) {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Request failed: ${response.status}`);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    const result = await response.json();
    // Unwrap the { success: true, data: T } envelope
    return result.data !== undefined ? result.data : result;
  }

  return {
    /**
     * List all workspaces for the current user
     */
    async listWorkspaces(): Promise<Workspace[]> {
      return fetchWithAuth('/workspaces');
    },

    /**
     * Create a new workspace
     */
    async createWorkspace(data: CreateWorkspaceRequest): Promise<Workspace> {
      return fetchWithAuth('/workspaces', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Get workspace details
     */
    async getWorkspace(workspaceId: string): Promise<Workspace> {
      return fetchWithAuth(`/workspaces/${workspaceId}`);
    },

    /**
     * Update workspace
     */
    async updateWorkspace(workspaceId: string, data: UpdateWorkspaceRequest): Promise<Workspace> {
      return fetchWithAuth(`/workspaces/${workspaceId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete workspace
     */
    async deleteWorkspace(workspaceId: string): Promise<void> {
      await fetchWithAuth(`/workspaces/${workspaceId}`, {
        method: 'DELETE',
      });
    },

    /**
     * List workspace members
     */
    async listMembers(workspaceId: string): Promise<WorkspaceMember[]> {
      return fetchWithAuth(`/workspaces/${workspaceId}/members`);
    },

    /**
     * Add member to workspace
     */
    async addMember(workspaceId: string, data: AddMemberRequest): Promise<WorkspaceMember> {
      return fetchWithAuth(`/workspaces/${workspaceId}/members`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  };
}

export type WorkspaceApi = ReturnType<typeof createWorkspaceApi>;
